/**
 * Recoil bridge for the AI agent drawer.
 *
 * Only the drawer-open flag lives in Recoil now — the chat state machine is
 * owned by the SDK (`@ai-sdk/react` `useChat`, see `src/agent`). The old
 * bespoke message/connection/proposal/model atoms and their types were
 * removed with the WebSocket bridge (recoverable from git).
 */
import { atom } from 'recoil';
import { recoilPersist } from 'recoil-persist';

export const chatDrawerOpenState = atom<boolean>({
    key: 'chatDrawerOpenState',
    default: false,
});

/**
 * A Contour+ model the clinician accepted in the agent chat. The Contour+
 * model-selection form consumes it once when it has the model list (pre-fills
 * the selection, and submits via its own Start path when `autoStart`), then
 * clears it. `null` = no agent request; the form behaves exactly as before.
 */
export interface AgentContourModelRequest {
    readonly modelName: string;
    readonly customizationName?: string;
    readonly autoStart: boolean;
}

export const agentContourModelRequestState =
    atom<AgentContourModelRequest | null>({
        key: 'agentContourModelRequestState',
        default: null,
    });

/**
 * Series the clinician accepted for loading in the agent chat. The DICOM
 * import dialog pre-selects them (patient → series) through its own
 * handlers, then clears this; the clinician still presses Load, so the
 * dialog's import and patient-conflict checks run unchanged. `null` = no
 * agent request; the dialog behaves exactly as before.
 */
export interface AgentDicomImportRequest {
    readonly patientId: string;
    readonly patientName: string;
    readonly targetName: string;
    readonly studyInstanceUid: string;
    readonly seriesInstanceUids: ReadonlyArray<string>;
}

export const agentDicomImportRequestState =
    atom<AgentDicomImportRequest | null>({
        key: 'agentDicomImportRequestState',
        default: null,
    });

// Clinician-preference persistence (non-PHI); corrupt or missing storage
// falls back to the atom default — the safe value.
const { persistAtom } = recoilPersist({
    key: 'mvision.agent',
    storage: localStorage,
    converter: JSON,
});

/**
 * Persisted clinician preference: send dictated text automatically (true) vs
 * surface it as an editable draft for review (false). SaMD-safe default is
 * OFF (review-before-send). Persistence is safe because the
 * toggle is always visible while chat is active and the redacted audit
 * stamps the mode per message; auto-send can never be silently armed.
 */
export const dictationAutoSendState = atom<boolean>({
    key: 'dictationAutoSendState',
    default: false,
    effects: [persistAtom],
});
